import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Clock, TrendingUp, TrendingDown } from "lucide-react";
import { PhotoViewerModal } from "./PhotoViewerModal";

interface MealSubmission {
  id: string;
  meal_item_id: string | null;
  scheduled_time: string | null;
  actual_time: string | null;
  photo_path: string;
  ai_calculated_calories: number | null;
  ai_detected_foods: any;
  status: string | null;
  submitted_at: string | null;
  day_number: number | null;
  meal_item?: {
    meal_name: string;
    meal_type: string;
    total_calories: number | null;
  };
}

interface SubmissionsListProps {
  submissions: MealSubmission[];
}

const dayNames: Record<number, string> = {
  1: "Segunda-feira",
  2: "Terça-feira",
  3: "Quarta-feira",
  4: "Quinta-feira",
  5: "Sexta-feira",
  6: "Sábado",
  7: "Domingo"
};

const mealTypeIcons: Record<string, string> = {
  breakfast: "🌅",
  snack: "🍎",
  lunch: "🍽️",
  dinner: "🌙",
  default: "🍴"
};

export function SubmissionsList({ submissions }: SubmissionsListProps) {
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);

  if (submissions.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <div className="rounded-full bg-muted p-6 mb-4">
            <Clock className="h-12 w-12 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-semibold mb-2">Nenhum registro ainda</h3>
          <p className="text-muted-foreground">
            Aguardando o paciente enviar as primeiras fotos das refeições
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {submissions.map((submission) => {
          const predictedCalories = submission.meal_item?.total_calories || 0;
          const actualCalories = submission.ai_calculated_calories || 0;
          const difference = actualCalories - predictedCalories;
          const percentDiff = predictedCalories > 0
            ? ((difference / predictedCalories) * 100).toFixed(0)
            : 0;

          const mealIcon = mealTypeIcons[submission.meal_item?.meal_type || "default"] || mealTypeIcons.default;
          const dayName = submission.day_number ? dayNames[submission.day_number] : "Dia não especificado";

          return (
            <Card key={submission.id} className="overflow-hidden hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    {/* Header */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-2xl">{mealIcon}</span>
                      <div>
                        <h4 className="font-semibold">
                          {dayName} • {submission.meal_item?.meal_name || "Refeição"}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {submission.actual_time
                            ? new Date(submission.actual_time).toLocaleString("pt-BR")
                            : new Date(submission.submitted_at || "").toLocaleString("pt-BR")}
                        </p>
                      </div>
                    </div>

                    {/* Calories comparison */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="space-y-1">
                        <p className="text-muted-foreground">Previsto</p>
                        <p className="font-bold text-lg">{predictedCalories} kcal</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-muted-foreground">Realizado</p>
                        <p className="font-bold text-lg flex items-center gap-2">
                          {actualCalories} kcal
                          {difference !== 0 && (
                            <Badge
                              variant={difference > 0 ? "destructive" : "default"}
                              className="text-xs"
                            >
                              {difference > 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                              {difference > 0 ? "+" : ""}{difference} ({percentDiff}%)
                            </Badge>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* AI detected foods */}
                    {submission.ai_detected_foods && (
                      <div className="pt-2 border-t">
                        <p className="text-xs text-muted-foreground mb-1">🤖 IA detectou:</p>
                        <p className="text-sm">
                          {typeof submission.ai_detected_foods === 'string'
                            ? submission.ai_detected_foods
                            : JSON.stringify(submission.ai_detected_foods)}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* View photo button */}
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setSelectedPhoto(submission.photo_path)}
                    className="flex-shrink-0"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {selectedPhoto && (
        <PhotoViewerModal
          photoPath={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
        />
      )}
    </>
  );
}
